import React, { useMemo, useState, useEffect } from 'react'
import { debounce } from 'lodash'
import {
  Box,
  Typography,
  Slider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { convertTo12Hour, getFormattedTime, stages } from '../Constant'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'

const styles: any = (theme: any, props: { chartWidth: number }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 20,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 20px',
    marginBottom: 10,
  },
  title: {
    fontWeight: 'bold',
    fontSize: 16,
    color: theme?.palette.text.primary,
  },
  timeRange: {
    fontWeight: 'bold',
    fontSize: 14,
    marginBottom: 10,
    color: theme?.palette.text.primary,
  },
  svgWrapper: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#2c2c2c',
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 20,
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    backgroundColor: '#2c2c2c',
    padding: 5,
  },
  legendItem: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 10px 0 10px',
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
    marginRight: 8,
  },
  legendText: {
    fontSize: 12,
    // color: theme?.palette.text.primary,
    color: '#ffffff',
  },
  bottomWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 8,
    width: props?.chartWidth + 31,
    padding: '0 4px',
  },
  bottomLabel: {
    fontSize: 12,
    color: theme?.palette.text.primary,
  },
  sliderWrapper: {
    marginTop: -44,
    width: props?.chartWidth,
    color: 'black',
  },
})

const parseSleepData = (rawData: any) => {
  return rawData?.map((item: any) => {
    const from = new Date(item?.start)
    const to = new Date(item?.end)
    const duration = (to.getTime() - from.getTime()) / (1000 * 60)
    return {
      stage: item?.stage,
      duration,
      item,
    }
  })
}

const getChartWidth = ({
  isXs,
  isSm,
  isMd,
  isLg,
  isXl,
}: {
  isXs: boolean
  isSm: boolean
  isMd: boolean
  isLg: boolean
  isXl: boolean
}): number => {
  switch (true) {
    case isXs:
      return 300
    case isSm:
      return 500
    case isMd:
      return 550
    case isLg:
      return 650
    case isXl:
      return 750
    default:
      return 600 // fallback
  }
}

const AllStagesTooltipContent = () => (
  <Box maxWidth={400} maxHeight={400} overflow='auto' p={1}>
    {stages?.map((stage: any, index: number) => (
      <Box
        key={index}
        mb={1.5}
        display={'flex'}
        justifyContent={'start'}
        flexDirection={'column'}
        gap={1.6}
      >
        <Typography variant='subtitle2' fontWeight={600}>
          {stage?.title}
        </Typography>
        <Typography variant='body2' fontSize={'12px'}>
          {stage?.description}
        </Typography>
        {stage.idealRange && (
          <Typography variant='caption' fontWeight={600}>
            Ideal Range: {stage?.idealRange}%
          </Typography>
        )}
        {index !== stages?.length - 1 && <Divider sx={{ mt: 1, mb: 1 }} />}
      </Box>
    ))}
  </Box>
)

interface Props {
  data: any[]
}

const Hypnogram: React.FC<Props> = ({ data }) => {
  const theme = useTheme()

  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const isSm = useMediaQuery(theme.breakpoints.only('sm'))
  const isMd = useMediaQuery(theme.breakpoints.only('md'))
  const isLg = useMediaQuery(theme.breakpoints.only('lg'))
  const isXl = useMediaQuery(theme.breakpoints.only('xl'))

  const CHART_HEIGHT = 140
  const STAGE_HEIGHT = CHART_HEIGHT / 5
  const CHART_WIDTH = getChartWidth({ isXs, isSm, isMd, isLg, isXl })

  const classes = useCustomStyles(styles, theme, { chartWidth: CHART_WIDTH })

  const sleepData = useMemo(() => parseSleepData(data), [data])
  const totalDuration = sleepData?.reduce(
    (sum: number, item: any) => sum + item.duration,
    0
  )

  const [sliderValue, setSliderValue] = useState(0)
  //   const [isDragging, setIsDragging] = useState(false)
  const [tooltip, setTooltip] = useState<any>(null)

  const sleepStages = [
    { name: 'AWAKE', color: '#FFECDC', level: 0 },
    { name: 'REM', color: '#D9C7F4', level: 1 },
    { name: 'LIGHT', color: '#B38EF8', level: 2 },
    { name: 'DEEP', color: '#7347F6', level: 3 },
  ]
  useEffect(() => {
    if (sleepData?.length > 0) {
      const firstItem = sleepData[0]
      setTooltip({
        x: 0,
        y: 0,
        stage: firstItem?.stage,
        duration: firstItem?.duration,
        item: firstItem,
      })
      setSliderValue(0)
    }
  }, [sleepData])

  // useEffect(() => {
  //   if (!tooltip && sleepData?.length > 0) {
  //     const firstItem = sleepData[0]
  //     setTooltip({
  //       x: 0,
  //       y: 0,
  //       stage: firstItem?.stage,
  //       duration: firstItem?.duration,
  //       item: firstItem,
  //     })
  //   }
  // }, [sleepData, tooltip])

  const updateTooltip = useMemo(() => {
    return debounce((value: number) => {
      let timePassed = 0
      for (let i = 0; i < sleepData?.length; i++) {
        const segment = sleepData[i]
        const nextTime = timePassed + segment?.duration
        if (value >= timePassed && value <= nextTime) {
          setTooltip({
            x: 0,
            y: 0,
            stage: segment?.stage,
            duration: segment?.duration,
            item: segment,
          })
          break
        }
        timePassed = nextTime
      }
    }, 10)
  }, [sleepData])

  const handleSliderChange = (event: Event, value: number | number[]) => {
    const v = typeof value === 'number' ? value : value[0]
    setSliderValue(v)
    updateTooltip(v)
  }

  const startTime = convertTo12Hour(
    getFormattedTime(tooltip?.item?.item?.start)
  )
  const endTime = convertTo12Hour(getFormattedTime(tooltip?.item?.item?.end))

  let currentX = 0
  const segments: JSX.Element[] = []

  sleepData?.forEach((item: any, index: number) => {
    const stageInfo = sleepStages?.find((s) => s?.name === item?.stage)
    if (!stageInfo) return

    const width = (item.duration / totalDuration) * CHART_WIDTH
    const y = stageInfo?.level * STAGE_HEIGHT
    const x = currentX
    currentX += width

    segments.push(
      <rect
        key={`segment-${index}`}
        x={x}
        y={y}
        width={width}
        height={STAGE_HEIGHT}
        fill={stageInfo?.color}
        onClick={() =>
          setTooltip({
            x: x + width / 2,
            y,
            stage: item?.stage,
            duration: item?.duration,
            item,
          })
        }
      />
    )
  })

  return (
    <Box>
      <Box className={classes.titleRow}>
        <Typography className={classes.title}>Sleep Stages</Typography>
        <Tooltip title={<AllStagesTooltipContent />} placement='left-start'>
          <InfoOutlinedIcon fontSize='small' />
        </Tooltip>
      </Box>

      <Box className={classes.container}>
        {data?.length > 0 && (
          <Typography className={classes.timeRange}>
            {`${tooltip?.stage} - ${startTime} to ${endTime}`}
          </Typography>
        )}

        <Box className={classes.svgWrapper}>
          <svg height={CHART_HEIGHT + 40} width={CHART_WIDTH}>
            {segments}
            {/* Marker line */}
            {totalDuration > 0 && (
              <line
                x1={(sliderValue / totalDuration) * CHART_WIDTH}
                y1={0}
                x2={(sliderValue / totalDuration) * CHART_WIDTH}
                y2={CHART_HEIGHT}
                stroke={'black'}
                strokeWidth={2}
              />
            )}
          </svg>
        </Box>

        <Slider
          size='small'
          className={classes.sliderWrapper}
          value={sliderValue}
          min={0}
          max={totalDuration}
          onChange={handleSliderChange}
          onChangeCommitted={(e, val) => {
            // setIsDragging(false)
            const v = typeof val === 'number' ? val : val?.[0]
            setSliderValue(v)
            updateTooltip(v)
          }}
          //   onMouseDown={() => setIsDragging(true)}
        />

        {/* <Box className={classes.bottomWrapper}>
          <Typography className={classes.bottomLabel}>{startedAt}</Typography>
          <Typography className={classes.bottomLabel}>{endAt}</Typography>
        </Box> */}

        <Box className={classes.legendContainer}>
          {sleepStages?.map((stage) => (
            <Box key={stage.name} className={classes.legendItem}>
              <Box className={classes.legendColor} bgcolor={stage?.color} />
              <Typography className={classes.legendText}>
                {stage?.name}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default Hypnogram
